import React, { useState } from 'react';
import { useId } from 'react-aria';
import { Group, Radio, RadioGroup } from 'react-aria-components';
import styled from 'styled-components';

import { Label } from './RatingsAndReviews';

const RadioGroupContainer = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  gap: var(--spectrum-global-dimension-size-300);
`;

const StarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 5px;
`;

const GroupContainer = styled(Group)`
  &[data-focus-within] {
    outline: 2px solid var(--spectrum-global-color-blue-400);
    outline-offset: 2px;
  }
`;

const StarRatingGroup = ({
  starCount = 5,
  label = 'Your rating',
  defaultValue,
  onRatingSet,
  isReadOnly = false,
  ratingFirst = false,
  ...other
}) => {
  const allRatings = Array.from(Array(starCount).keys()).map((i) => String(i + 1));

  // Track which rating is hovered at the group level.
  const [hoveredRating, setHoveredRating] = useState(undefined);
  const [selectedRating, setSelectedRating] = useState(String(defaultValue));

  // Generate a unique ID for the label
  const labelId = useId();

  const onChangeRadioGroup = (value) => {
    setSelectedRating(value);
    onRatingSet(Number(value));
  };

  const onPointerOver = (e) => {
    if (e.target.dataset?.rating) {
      setHoveredRating(e.target.dataset.rating);
    }
  };

  const onPointerOut = () => {
    setHoveredRating(undefined);
  };

  return (
    <RadioGroupContainer
      orientation="horizontal"
      {...(defaultValue !== 0 ? { value: selectedRating } : {})}
      onChange={(value) => onChangeRadioGroup(value)}
      isReadOnly={isReadOnly}
      aria-labelledby={labelId}
      {...other}
      {...(ratingFirst ? { style: { flexDirection: 'column-reverse', alignItems: 'center', gap: 0 } } : {})}
    >
      {({ state }) => (
        <>
          <Label id={labelId}>{label}</Label>
          <GroupContainer aria-labelledby={labelId} data-name="star-rating-group">
            <StarContainer data-name="star-rating-group" onPointerOver={onPointerOver} onPointerLeave={onPointerOut}>
              {allRatings.map((rating) => (
                <StarRating
                  key={rating}
                  rating={rating}
                  selected={state.selectedValue}
                  hoveredRating={hoveredRating}
                  isReadOnly={isReadOnly}
                />
              ))}
            </StarContainer>
          </GroupContainer>
        </>
      )}
    </RadioGroupContainer>
  );
};

export const StarRating = ({ rating, selected, hoveredRating, isReadOnly }) => {
  const ratingNum = Number(rating);
  const selectedNum = Number(selected);
  const isFilled =
    hoveredRating !== undefined && !isReadOnly ? ratingNum <= Number(hoveredRating) : ratingNum <= selectedNum;

  const getFillColor = (isHovered) => {
    if (!isReadOnly) {
      if (isHovered) return 'var(--spectrum-global-color-gray-700)';
      if (selected && hoveredRating === undefined) return 'var(--spectrum-global-color-blue-500)';
      return 'var(--spectrum-global-color-gray-500)';
    } else {
      return 'var(--spectrum-global-color-blue-500)';
    }
  };

  return (
    <Radio aria-label={`${rating} stars`} value={rating}>
      {({ isHovered }) => (
        <>
          <svg data-rating={rating} xmlns="http://www.w3.org/2000/svg" width={18} height={18}>
            {isFilled ? (
              <path
                d="m9.241.3 2.161 5.715 6.106.289a.255.255 0 0 1 .147.454l-4.77 3.823 1.612 5.9a.255.255 0 0 1-.386.28L9.002 13.4l-5.11 3.358a.255.255 0 0 1-.386-.28l1.612-5.9-4.77-3.821A.255.255 0 0 1 .495 6.3l6.107-.285L8.763.3a.255.255 0 0 1 .478 0Z"
                fill={getFillColor(isHovered)}
              />
            ) : (
              <path
                d="m9.031 2.541 1.777 4.753 5.11.241-3.987 3.2 1.336 4.913-4.266-2.782-4.282 2.808 1.352-4.937-3.987-3.2 5.1-.245ZM9.042.412a.369.369 0 0 0-.349.239L6.486 6.326l-6.1.293a.375.375 0 0 0-.217.667l4.762 3.821L3.318 17a.376.376 0 0 0 .362.475.371.371 0 0 0 .2-.063l5.121-3.351 5.095 3.324a.371.371 0 0 0 .2.062.376.376 0 0 0 .363-.475l-1.595-5.866 4.767-3.826a.375.375 0 0 0-.217-.667l-6.1-.287L9.393.655a.369.369 0 0 0-.351-.243Z"
                fill="var(--spectrum-global-color-gray-500)"
              />
            )}
          </svg>
        </>
      )}
    </Radio>
  );
};

export default StarRatingGroup;
